import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { client, MinimalEntityDto } from "@/client";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useTable } from "@/components/custom/data-table/data-table-context";
import { Input } from "@/components/ui/input";
import SelectInput, {
  OptionType,
} from "@/components/select-input/select-input.tsx";
import { usePermissions } from "@/hooks/use-permissions";
import DialogComponent from "@/components/custom/dialog.tsx";
import { architectureEnum, collaboratorOption } from "./-schema-exports";
import { GroupBase } from "react-select";

import { showActionErrorToast } from "@/state/error-toast";

export const AddCluster = () => {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  if (permissions.clusters.create) {
    return (
      <DialogComponent
        label={t("addCluster")}
        title={t("addNewCluster")}
        contentClassName={"max-w-[50vh]"}
      >
        <AddClusterForm />
      </DialogComponent>
    );
  }
  return <></>;
};

const formSchema = z
  .object({
    clusterName: z.string().min(1, { message: "Please enter cluster name" }),
    architecture: architectureEnum,
    deviceIds: z.array(z.object({ value: z.number(), label: z.string() })),
    serverCollaborator: z.optional(collaboratorOption),
  })
  .refine(
    (data) =>
      data.architecture.value === "distributed" || data.serverCollaborator,
    {
      message:
        "In site collaborator must be provided for Centralized and Hybrid architectures",
      path: ["serverCollaborator"],
    },
  );

const AddClusterForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: allDevicesMinimalResults } = useQuery({
    ...client.devices.listMinimal
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
  });
  const { data: unassignedDevicesResults } = useQuery({
    ...client.devices.listMinimal
      .assign({
        page: 1,
        limit: 9999,
        clusterId: -1,
      })
      .asQueryOptions({}),
  });

  const { groupedOptions } = useMemo(() => {
    //
    const allDevicesMap = new Map(
      (allDevicesMinimalResults?.results ?? []).map(
        (device: MinimalEntityDto) => [device.id, device],
      ),
    );
    (unassignedDevicesResults?.results ?? []).forEach(
      (device: MinimalEntityDto) => allDevicesMap.delete(device.id),
    );
    const devicesRest: OptionType[] = [...allDevicesMap.values()].map(
      (value) => ({ label: value.name, value: value.id }),
    );
    //
    const unassignedDevices: OptionType[] = (
      unassignedDevicesResults?.results ?? []
    )
      .map((result) => {
        if (
          result !== null &&
          typeof result === "object" &&
          "id" in result &&
          "name" in result
        ) {
          return {
            label: result.name as any as string,
            value: result.id as any as number,
          };
        } else {
          return null;
        }
      })
      .filter((element) => {
        return element !== null;
      })
      .sort((a, b) => a.value - b.value);
    //
    const groupedOptions: readonly GroupBase<{
      value: number;
      label: string;
    }>[] = [
      {
        label: t("unassignedDevices"),
        options: unassignedDevices as readonly {
          value: number;
          label: string;
        }[],
      },
      {
        label: t("assignedDevices"),
        options: devicesRest as readonly { value: number; label: string }[],
      },
    ];

    return {
      groupedOptions,
    };
  }, [allDevicesMinimalResults, unassignedDevicesResults]);

  const createCluster = useMutation({
    ...client.clusters.create.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });
  const table = useTable();
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      architecture: { label: "Distributed", value: "distributed" },
      deviceIds: [],
    },
  });
  const collaboratorOptions = form.watch("deviceIds", []);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const cluster = await createCluster.mutateAsync({
      requestBody: {
        name: data.clusterName,
        deviceIds: data.deviceIds.map((d) => d.value),
        architecture: data.architecture.value,
        serverDeviceId: data.serverCollaborator?.value,
      },
    });
    table?.setPreview(2);
    table?.setPosition({});
    queryClient.invalidateQueries({
      queryKey: client.clusters.listSummaries.queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: client.clusters.listMinimal.queryKey,
    });
    navigate({
      to: "/dashboard/resources/clusters/$clusterId",
      params: { clusterId: cluster.id as any },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="pb-4 flex flex-col gap-4">
          <FormField
            control={form.control}
            name="clusterName"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>{t("clusterName")}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t("clusterName")} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="architecture"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>{"Architecture"}</FormLabel>
                <FormControl>
                  <SelectInput
                    {...field}
                    isMulti={false}
                    isClearable={false}
                    options={[
                      { label: "Distributed", value: "distributed" },
                      { label: "Hybrid", value: "hybrid" },
                      { label: "Centralized", value: "centralized" },
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="deviceIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("associateDevices")}</FormLabel>
                <FormControl>
                  <SelectInput
                    placeholder={t("selectDevices")}
                    {...field}
                    classNames={{
                      control: () => "min-h-16 max-h-28 overflow-y-scroll",
                    }}
                    isMulti={true}
                    isClearable={true}
                    options={groupedOptions}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div hidden={form.watch("architecture").value === "distributed"}>
            <FormField
              control={form.control}
              name="serverCollaborator"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("inSiteCollaborator")}</FormLabel>
                  <FormControl>
                    <SelectInput
                      {...field}
                      isMulti={false}
                      isClearable={true}
                      isSearchable={true}
                      options={collaboratorOptions}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">{t("cancel")}</Button>
          </DialogClose>
          <Button
            variant={"azure"}
            disabled={!form.formState.isDirty || !form.formState.isValid}
          >
            {t("done")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
